/**
 * YouTube watch links for official lottery draw uploads (display + resolve helpers).
 */

export type YoutubeVideoSource = 'auto' | 'manual';

const VIDEO_ID_RE = /^[A-Za-z0-9_-]{11}$/;

/**
 * Extract an 11-char YouTube video id from a raw id or common watch/share URLs.
 */
export function parseYoutubeVideoId(input: string | null | undefined): string | null {
  const raw = String(input ?? '').trim();
  if (!raw) return null;
  if (VIDEO_ID_RE.test(raw)) return raw;

  try {
    const url = new URL(raw);
    const host = url.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = url.pathname.split('/').filter(Boolean)[0] ?? '';
      return VIDEO_ID_RE.test(id) ? id : null;
    }
    if (
      host === 'youtube.com'
      || host === 'm.youtube.com'
      || host === 'music.youtube.com'
      || host === 'youtube-nocookie.com'
    ) {
      const v = url.searchParams.get('v');
      if (v && VIDEO_ID_RE.test(v)) return v;
      const parts = url.pathname.split('/').filter(Boolean);
      if (parts[0] === 'embed' || parts[0] === 'shorts' || parts[0] === 'live') {
        const id = parts[1] ?? '';
        return VIDEO_ID_RE.test(id) ? id : null;
      }
    }
  } catch {
    return null;
  }
  return null;
}

export function youtubeWatchUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

/** Privacy-enhanced embed for the web portal (not used in the native shell). */
export function youtubeEmbedUrl(videoId: string): string | null {
  const id = parseYoutubeVideoId(videoId);
  return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
}

/** Official Florida Lottery channel uploads. */
export const FLORIDA_LOTTERY_YOUTUBE_CHANNEL_ID = 'UCPm7mcdzUK9PjQtdGX4_Niw';

/**
 * Search query matching Florida Lottery Pick Family upload titles
 * (e.g. "Pick EVENING 08-10-2026").
 */
export function floridaDrawYoutubeSearchQuery(
  dayKey: string,
  window: 'day' | 'night',
): string {
  const [y, m, d] = dayKey.split('-');
  const stamped = `${m}-${d}-${y}`;
  const session = window === 'night' ? 'EVENING' : 'MIDDAY';
  return `Pick ${session} ${stamped}`;
}

export function findYoutubeVideoIdForDraw(
  draws: readonly { dayKey: string; window: string; youtubeVideoId?: string | null }[],
  dayKey: string,
  window: 'day' | 'night',
): string | null {
  const row = draws.find((d) => d.dayKey === dayKey && d.window === window);
  const id = parseYoutubeVideoId(row?.youtubeVideoId ?? null);
  return id;
}
